// shadow.ts — Analytic sphere shadows (eclipses) without a shadow map. Every body
// in a star system is a sphere lit by a single star, so "is this surface point in
// shadow?" is a ray–sphere question, not a depth-buffer one: from the point we
// look at the star's angular disk and each occluder sphere's angular disk and dim
// the direct sunlight by how much the occluder covers the star (umbra → penumbra).
// That is a handful of ALU ops per fragment, versus a point light's 6-face shadow
// cube map (six extra scene renders every frame) — the thing that was killing fps.
//
// Usage: build uniforms with makeShadowUniforms(), refresh them each frame with
// setOccluders(), and inject the GLSL — into a MeshStandardMaterial via
// standardShadowPatch()/onBeforeCompile, or into a custom shader by splicing
// SHADOW_PARS + a sunVisibility(P) call into the fragment source.
import { Vector3, Vector4 } from 'three';
import type { IUniform, WebGLProgramParametersWithUniforms } from 'three';

// A planet rarely has more than a few moons whose disks ever cross the star; cap
// the loop so the array size stays a GLSL compile-time constant.
export const MAX_OCCLUDERS = 4;

export interface Occluder {
  pos: Vector3;
  radius: number;
}

export interface ShadowUniforms {
  uSunPosW: IUniform<Vector3>;
  uSunRadius: IUniform<number>;
  uOccluders: IUniform<Vector4[]>; // xyz = world centre, w = radius
  uOccluderCount: IUniform<number>;
}

export function makeShadowUniforms(): ShadowUniforms {
  const occ: Vector4[] = [];
  for (let i = 0; i < MAX_OCCLUDERS; i++) {
    occ.push(new Vector4());
  }
  return {
    uSunPosW: { value: new Vector3() },
    uSunRadius: { value: 1 },
    uOccluders: { value: occ },
    uOccluderCount: { value: 0 },
  };
}

// Refresh the uniforms for this frame. The star is the light; `list` are the
// spheres that might eclipse it (true radii — close up, where eclipses read, the
// apparent-size floor is inactive, so true radii line up with what's drawn).
export function setOccluders(
  u: ShadowUniforms,
  sun: Vector3,
  sunRadius: number,
  list: Occluder[],
): void {
  u.uSunPosW.value.copy(sun);
  u.uSunRadius.value = sunRadius;
  const n = Math.min(list.length, MAX_OCCLUDERS);
  for (let i = 0; i < n; i++) {
    const o = list[i];
    u.uOccluders.value[i].set(o.pos.x, o.pos.y, o.pos.z, o.radius);
  }
  u.uOccluderCount.value = n;
}

// GLSL declarations + sunVisibility(P): returns 1 in full sun, 0 in full umbra,
// a smooth penumbra between (and an annular partial when the occluder's disk is
// smaller than the star's). Drop this into any fragment shader that has a world
// position to test.
export const SHADOW_PARS = /* glsl */ `
  uniform vec3 uSunPosW;
  uniform float uSunRadius;
  uniform vec4 uOccluders[${MAX_OCCLUDERS}];
  uniform int uOccluderCount;

  float sunVisibility(vec3 P) {
    vec3 L = uSunPosW - P;
    float dL = length(L);
    if (dL < 1e-4) {
      return 1.0;
    }
    vec3 Ld = L / dL;
    float sunAng = uSunRadius / dL; // angular radius of the star at P
    float vis = 1.0;
    for (int i = 0; i < ${MAX_OCCLUDERS}; i++) {
      if (i >= uOccluderCount) {
        break;
      }
      vec3 toC = uOccluders[i].xyz - P;
      float r = uOccluders[i].w;
      float tc = dot(toC, Ld);
      if (tc <= 0.0 || tc >= dL) {
        continue; // occluder behind the fragment, or beyond the star
      }
      float perp = length(toC - Ld * tc);
      float occAng = r / tc;     // angular radius of the occluder disk
      float sepAng = perp / tc;  // angular separation of the two disks
      // Largest fraction of the star this disk can hide (annular if < 1).
      float coverage = clamp((occAng * occAng) / (sunAng * sunAng), 0.0, 1.0);
      // 1 when the disks fully overlap, 0 once they no longer touch.
      float overlap = 1.0 - smoothstep(max(occAng - sunAng, 0.0), occAng + sunAng, sepAng);
      vis *= 1.0 - coverage * overlap;
    }
    return vis;
  }
`;

// Patch a MeshStandardMaterial (via material.onBeforeCompile) so its direct
// sunlight is multiplied by sunVisibility — only the star's contribution is
// eclipsed; ambient/emissive are untouched, so an eclipsed face goes dim, not
// black. Pass the same uniforms object you feed to setOccluders each frame.
export function standardShadowPatch(uniforms: ShadowUniforms) {
  return (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uSunPosW = uniforms.uSunPosW;
    shader.uniforms.uSunRadius = uniforms.uSunRadius;
    shader.uniforms.uOccluders = uniforms.uOccluders;
    shader.uniforms.uOccluderCount = uniforms.uOccluderCount;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vShadowWorld;')
      .replace(
        '#include <worldpos_vertex>',
        '#include <worldpos_vertex>\n  vShadowWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vShadowWorld;\n${SHADOW_PARS}`)
      .replace(
        '#include <lights_fragment_begin>',
        `#include <lights_fragment_begin>
        {
          float _vis = sunVisibility(vShadowWorld);
          reflectedLight.directDiffuse *= _vis;
          reflectedLight.directSpecular *= _vis;
        }`,
      );
  };
}
